import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, ShoppingCart, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [salesSearchQuery, setSalesSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [clientInputMode, setClientInputMode] = useState('manual'); // 'manual' or 'select'
  const [clienteId, setClienteId] = useState('');
  const [clienteNombre, setClienteNombre] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAnnulModalOpen, setIsAnnulModalOpen] = useState(false);
  const [saleToAnnul, setSaleToAnnul] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchSales();
    fetchProducts();
    fetchClients();
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, products]);

  useEffect(() => {
    filterSales();
  }, [sales, currentUser, salesSearchQuery]);

  const isToday = (dateString) => {
    const saleDate = new Date(dateString);
    const today = new Date();
    return (
      saleDate.getDate() === today.getDate() &&
      saleDate.getMonth() === today.getMonth() &&
      saleDate.getFullYear() === today.getFullYear()
    );
  };

  const filterSales = () => {
    let filtered = sales;

    // 1. Filtrar solo ventas del día actual
    filtered = filtered.filter(sale => isToday(sale.created_at));

    // 2. Si es vendedor, solo mostrar sus ventas
    if (currentUser && currentUser.role === 'vendedor') {
      filtered = filtered.filter(sale => sale.created_by === currentUser.username);
    }

    // 3. Aplicar búsqueda si hay texto
    if (salesSearchQuery.trim()) {
      const query = salesSearchQuery.toLowerCase();
      filtered = filtered.filter(sale => {
        const saleId = sale.sale_number ? sale.sale_number.toString() : sale.id.slice(0, 8);
        const amount = sale.total_amount.toFixed(2);
        const paymentMethod = sale.payment_method.toLowerCase();
        const createdBy = sale.created_by.toLowerCase();
        const clientName = (sale.cliente_nombre || sale.client_name || '').toLowerCase();
        const saleDate = new Date(sale.created_at).toLocaleDateString('es-SV').toLowerCase();
        
        return (
          saleId.includes(query) ||
          amount.includes(query) ||
          paymentMethod.includes(query) ||
          createdBy.includes(query) ||
          clientName.includes(query) ||
          saleDate.includes(query)
        );
      });
    }

    setFilteredSales(filtered);
  };

  const fetchSales = async () => {
    try {
      const response = await axios.get('/sales');
      setSales(response.data);
    } catch (error) {
      toast.error('Error al cargar ventas');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const calculateItemTotal = (quantity, unitPrice, discountType, discountValue) => {
    const subtotal = quantity * unitPrice;
    if (!discountType || !discountValue) return subtotal;
    
    if (discountType === 'percentage') {
      return subtotal - (subtotal * discountValue / 100);
    } else if (discountType === 'amount') {
      return Math.max(0, subtotal - discountValue);
    }
    return subtotal;
  };

  const addToCart = (product) => {
    // Validación 1: Verificar stock ANTES de agregar
    if (product.stock <= 0) {
      toast.error(`${product.name} no tiene stock disponible`);
      return;
    }

    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      if (existingItem.quantity < product.stock) {
        setCart(cart.map(item => 
          item.product_id === product.id 
            ? {
                ...item, 
                quantity: item.quantity + 1, 
                total: calculateItemTotal(item.quantity + 1, item.unit_price, item.discount_type, item.discount_value)
              }
            : item
        ));
      } else {
        toast.error('Stock insuficiente');
      }
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.sale_price,
        min_sale_price: product.min_sale_price, // Guardar precio mínimo
        discount_type: null,
        discount_value: 0,
        total: product.sale_price,
      }]);
    }
    setSearchQuery('');
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    const product = products.find(p => p.id === productId);
    
    if (newQuantity > product.stock) {
      toast.error('Stock insuficiente');
      return;
    }

    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item => 
      item.product_id === productId
        ? {
            ...item, 
            quantity: newQuantity, 
            total: calculateItemTotal(newQuantity, item.unit_price, item.discount_type, item.discount_value)
          }
        : item
    ));
  };

  const updateDiscount = (productId, discountType, discountValue) => {
    const item = cart.find(i => i.product_id === productId);
    if (!item) return;

    const parsedValue = parseFloat(discountValue) || 0;
    
    // Calcular el precio final por unidad con el descuento
    const subtotal = item.quantity * item.unit_price;
    let finalTotal = subtotal;
    
    if (discountType === 'percentage') {
      finalTotal = subtotal - (subtotal * parsedValue / 100);
    } else if (discountType === 'amount') {
      finalTotal = Math.max(0, subtotal - parsedValue);
    }
    
    // Calcular precio por unidad después del descuento
    const pricePerUnit = finalTotal / item.quantity;
    
    // Validación 2: Verificar que el precio por unidad no sea menor al precio mínimo
    if (pricePerUnit < item.min_sale_price) {
      toast.error(
        `El precio con descuento ($${pricePerUnit.toFixed(2)}) no puede ser menor al precio mínimo ($${item.min_sale_price.toFixed(2)})`
      );
      return;
    }

    setCart(cart.map(cartItem => 
      cartItem.product_id === productId
        ? {
            ...cartItem,
            discount_type: discountType,
            discount_value: parsedValue,
            total: finalTotal
          }
        : cartItem
    ));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmitSale = async () => {
    if (cart.length === 0) {
      toast.error('Agrega productos al carrito');
      return;
    }

    setLoading(true);
    try {
      const saleData = {
        items: cart,
        total_amount: calculateTotal(),
        payment_method: paymentMethod,
      };

      // Add client information
      if (clientInputMode === 'select' && clienteId && clienteId !== 'none') {
        saleData.cliente_id = clienteId;
      } else if (clienteNombre.trim()) {
        saleData.cliente_nombre = clienteNombre.trim();
      }

      await axios.post('/sales', saleData);

      toast.success('Venta registrada exitosamente');
      setCart([]);
      setClienteId('');
      setClienteNombre('');
      setClientInputMode('manual');
      setIsDialogOpen(false);
      fetchSales();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar venta');
    } finally {
      setLoading(false);
    }
  };

  const openAnnulModal = (sale) => {
    setSaleToAnnul(sale);
    setIsAnnulModalOpen(true);
  };

  const canAnnulSale = (sale) => {
    // No se puede anular una venta ya anulada
    if (sale.status === 'voided') {
      return false;
    }
    
    // Si no hay usuario logueado, no mostrar botón
    if (!currentUser) {
      return false;
    }
    
    // Si es admin, puede anular cualquier venta
    if (currentUser.role === 'admin') {
      return true;
    }
    
    // Si es vendedor, solo puede anular sus propias ventas
    if (currentUser.role === 'vendedor' && sale.created_by === currentUser.username) {
      return true;
    }
    
    return false;
  };

  const handleAnnulSale = async () => {
    if (!saleToAnnul) return;

    setLoading(true);
    try {
      await axios.post(`/sales/${saleToAnnul.id}/void`);
      toast.success('Venta anulada exitosamente');
      setIsAnnulModalOpen(false);
      setSaleToAnnul(null);
      fetchSales();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular venta');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (sale) => {
    setSaleToDelete(sale);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`/sales/${saleToDelete.id}`);
      toast.success('Venta eliminada permanentemente');
      setIsDeleteModalOpen(false);
      setSaleToDelete(null);
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar venta');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="sales-page" data-testid="sales-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Ventas</h1>
          <p className="page-subtitle">Registra y consulta ventas del día</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} data-testid="new-sale-btn">
          <Plus size={20} />
          Nueva Venta
        </Button>
      </div>

      {/* Search Box for Sales */}
      <Card className="sales-search-card">
        <div className="sales-search-container">
          <Label htmlFor="sales-search">Buscar venta del día</Label>
          <div className="sales-search-box">
            <Search size={20} className="search-icon" />
            <Input
              id="sales-search"
              type="text"
              placeholder="Buscar por número, cliente, monto, fecha, método de pago o vendedor..."
              value={salesSearchQuery}
              onChange={(e) => setSalesSearchQuery(e.target.value)}
              className="sales-search-input"
              data-testid="sales-search-input"
            />
          </div>
          <p className="sales-count">
            Mostrando {filteredSales.length} {filteredSales.length === 1 ? 'venta' : 'ventas'} del día
            {currentUser && currentUser.role === 'vendedor' && ' (tus ventas)'}
          </p>
        </div>
      </Card>

      <div className="sales-list">
        {filteredSales.map((sale) => (
          <Card key={sale.id} className="sale-card" data-testid={`sale-card-${sale.id}`}>
            <div className="sale-header">
              <div>
                <h3 className="sale-id">
                  Venta #{sale.sale_number || sale.id.slice(0, 8)}
                  {sale.status === 'voided' && (
                    <span className="annulled-badge">Anulado</span>
                  )}
                </h3>
                <p className="sale-date">{new Date(sale.created_at).toLocaleString()}</p>
              </div>
              <div className="sale-total">${sale.total_amount.toFixed(2)}</div>
            </div>
            
            <div className="sale-items">
              {sale.items.map((item, idx) => (
                <div key={idx} className="sale-item">
                  <span>{item.product_name} x{item.quantity}</span>
                  <span>${item.total.toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <div className="sale-footer">
              <div className="sale-footer-left">
                <span className="payment-badge">{sale.payment_method}</span>
                <span className="sale-user">Por: {sale.created_by}</span>
                {(sale.cliente_nombre || sale.client_name) && (
                  <span className="client-badge">
                    Cliente: {sale.cliente_nombre || sale.client_name}
                  </span>
                )}
              </div>
              <div className="sale-footer-actions">
                {canAnnulSale(sale) && (
                  <Button
                    size="sm"
                    onClick={() => openAnnulModal(sale)}
                    className="annul-button-warning"
                    data-testid={`annul-button-${sale.id}`}
                  >
                    <XCircle size={16} />
                    Anular
                  </Button>
                )}
                {sale.status === 'voided' && currentUser && currentUser.role === 'admin' && (
                  <Button
                    size="sm"
                    onClick={() => openDeleteModal(sale)}
                    className="delete-button-danger"
                    data-testid={`delete-button-${sale.id}`}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredSales.length === 0 && (
        <div className="empty-state">
          <ShoppingCart size={64} />
          <h3>No hay ventas del día</h3>
          <p>
            {salesSearchQuery.trim() 
              ? 'No se encontraron ventas con ese criterio de búsqueda'
              : currentUser && currentUser.role === 'vendedor' 
                ? 'Aún no has realizado ventas hoy'
                : 'No se han registrado ventas hoy'}
          </p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sale-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Venta</DialogTitle>
          </DialogHeader>

          <div className="sale-form">
            <div className="search-section">
              <Label>Buscar Producto</Label>
              <div className="search-box">
                <Search size={20} className="search-icon" />
                <Input
                  type="text"
                  placeholder="Nombre o código..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  data-testid="sale-search-input"
                />
              </div>
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((product) => (
                    <div 
                      key={product.id} 
                      className={`search-result-item ${product.stock <= 0 ? 'out-of-stock' : ''}`}
                      onClick={() => addToCart(product)}
                      data-testid={`add-product-${product.id}`}
                    >
                      <div>
                        <div className="result-name">{product.name}</div>
                        <div className="result-info">
                          <span className="result-code">{product.code}</span>
                          <span className={`result-stock ${product.stock <= 0 ? 'no-stock' : ''}`}>
                            Stock: {product.stock}
                          </span>
                        </div>
                      </div>
                      <div className="result-price-container">
                        <div className="result-price">${product.sale_price.toFixed(2)}</div>
                        {product.stock <= 0 && (
                          <span className="no-stock-badge">Sin Stock</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Client Section */}
            <div className="client-section">
              <div className="client-header">
                <Label>Cliente</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setClientInputMode(clientInputMode === 'manual' ? 'select' : 'manual');
                    setClienteId('');
                    setClienteNombre('');
                  }}
                  className="toggle-client-btn"
                >
                  {clientInputMode === 'manual' ? 'Seleccionar de lista' : 'Ingresar manualmente'}
                </Button>
              </div>

              {clientInputMode === 'manual' ? (
                <Input
                  type="text"
                  placeholder="Nombre del cliente (opcional)..."
                  value={clienteNombre}
                  onChange={(e) => setClienteNombre(e.target.value)}
                  className="client-input"
                  data-testid="client-name-input"
                />
              ) : (
                <Select value={clienteId} onValueChange={setClienteId}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Selecciona un cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="cart-section">
              <h3 className="cart-title">Carrito</h3>
              
              {cart.length === 0 ? (
                <div className="cart-empty">Agrega productos al carrito</div>
              ) : (
                <div className="cart-items">
                  {cart.map((item) => (
                    <div key={item.product_id} className="cart-item-wrapper" data-testid={`cart-item-${item.product_id}`}>
                      <div className="cart-item">
                        <div className="cart-item-info">
                          <div className="cart-item-name">{item.product_name}</div>
                          <div className="cart-item-price">${item.unit_price.toFixed(2)} c/u</div>
                        </div>
                        <div className="cart-item-controls">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value))}
                            className="quantity-input"
                            data-testid={`quantity-${item.product_id}`}
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeFromCart(item.product_id)}
                            data-testid={`remove-${item.product_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="cart-item-total">${item.total.toFixed(2)}</div>
                      </div>
                      
                      {/* Discount Controls */}
                      <div className="discount-controls">
                        <Label className="discount-label">Descuento:</Label>
                        <Select 
                          value={item.discount_type || 'none'}
                          onValueChange={(value) => {
                            if (value === 'none') {
                              updateDiscount(item.product_id, null, 0);
                            } else {
                              updateDiscount(item.product_id, value, item.discount_value);
                            }
                          }}
                        >
                          <SelectTrigger className="discount-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin descuento</SelectItem>
                            <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                            <SelectItem value="amount">Monto ($)</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {item.discount_type && (
                          <Input
                            type="number"
                            min="0"
                            step={item.discount_type === 'percentage' ? '1' : '0.01'}
                            max={item.discount_type === 'percentage' ? '100' : undefined}
                            placeholder={item.discount_type === 'percentage' ? '% descuento' : '$ descuento'}
                            value={item.discount_value || ''}
                            onChange={(e) => updateDiscount(item.product_id, item.discount_type, e.target.value)}
                            className="discount-value-input"
                            data-testid={`discount-${item.product_id}`}
                          />
                        )}
                        
                        {item.discount_type && item.discount_value > 0 && (
                          <span className="discount-badge">
                            {item.discount_type === 'percentage' 
                              ? `-${item.discount_value}%` 
                              : `-$${item.discount_value.toFixed(2)}`}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="payment-section">
              <Label>Método de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="total-section">
              <span className="total-label">Total:</span>
              <span className="total-amount" data-testid="total-amount">${calculateTotal().toFixed(2)}</span>
            </div>

            <div className="form-actions">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmitSale} disabled={loading} data-testid="complete-sale-btn">
                {loading ? 'Procesando...' : 'Completar Venta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Annul Sale Confirmation Modal */}
      <Dialog open={isAnnulModalOpen} onOpenChange={setIsAnnulModalOpen}>
        <DialogContent className="annul-modal">
          <DialogHeader>
            <DialogTitle>Anular Venta</DialogTitle>
          </DialogHeader>
          <div className="annul-modal-content">
            <p className="annul-warning-text">
              ¿Deseas anular esta venta? Esta acción no se puede deshacer.
            </p>
            {saleToAnnul && (
              <div className="sale-info-box">
                <p><strong>Venta:</strong> #{saleToAnnul.sale_number || saleToAnnul.id.slice(0, 8)}</p>
                <p><strong>Monto:</strong> ${saleToAnnul.total_amount.toFixed(2)}</p>
                <p><strong>Fecha:</strong> {new Date(saleToAnnul.created_at).toLocaleString()}</p>
              </div>
            )}
            <div className="annul-modal-actions">
              <Button 
                variant="outline" 
                onClick={() => setIsAnnulModalOpen(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                className="confirm-annul-button"
                onClick={handleAnnulSale}
                disabled={loading}
                data-testid="confirm-annul-btn"
              >
                {loading ? 'Anulando...' : 'Confirmar Anulación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Voided Sale Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="delete-modal">
          <DialogHeader>
            <DialogTitle>Eliminar Venta Anulada</DialogTitle>
          </DialogHeader>
          <div className="delete-modal-content">
            <p className="delete-warning-text">
              ⚠️ Esta acción eliminará permanentemente la venta anulada. No se puede deshacer.
            </p>
            {saleToDelete && (
              <div className="sale-info-box">
                <p><strong>Venta:</strong> #{saleToDelete.sale_number || saleToDelete.id.slice(0, 8)}</p>
                <p><strong>Monto:</strong> ${saleToDelete.total_amount.toFixed(2)}</p>
                <p><strong>Fecha:</strong> {new Date(saleToDelete.created_at).toLocaleString()}</p>
                <p><strong>Vendedor:</strong> {saleToDelete.created_by}</p>
              </div>
            )}
            <div className="delete-modal-actions">
              <Button 
                variant="outline" 
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                className="confirm-delete-button"
                onClick={handleDeleteSale}
                disabled={loading}
                data-testid="confirm-delete-btn"
              >
                {loading ? 'Eliminando...' : 'Confirmar Eliminación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <style jsx>{`
        .sales-page {
          max-width: 1400px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .sales-search-card {
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .sales-search-container {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .sales-search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .sales-search-box .search-icon {
          position: absolute;
          left: 1rem;
          color: #94a3b8;
          pointer-events: none;
        }

        .sales-search-input {
          padding-left: 3rem;
          width: 100%;
        }

        .sales-count {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .sales-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .sale-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .sale-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .sale-id {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .sale-date {
          font-size: 0.875rem;
          color: #64748b;
        }

        .sale-total {
          font-size: 1.5rem;
          font-weight: 800;
          color: #10b981;
        }

        .sale-items {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .sale-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.9375rem;
          color: #64748b;
        }

        .sale-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .sale-footer-left {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .sale-footer-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .annul-button-warning {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .annul-button-warning:hover {
          background: #d97706;
        }

        .delete-button-danger {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .delete-button-danger:hover {
          background: #991b1b;
        }

        .annul-button {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #ef4444;
          color: white;
        }

        .annul-button:hover {
          background: #dc2626;
        }

        .annulled-badge {
          margin-left: 0.75rem;
          padding: 0.25rem 0.75rem;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .payment-badge {
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .client-badge {
          padding: 0.25rem 0.75rem;
          background: #fef3c7;
          color: #92400e;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .sale-user {
          font-size: 0.875rem;
          color: #64748b;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .sale-dialog {
          max-width: 650px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .sale-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .search-section {
          position: relative;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .search-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-top: 0.5rem;
          max-height: 300px;
          overflow-y: auto;
          z-index: 50;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .search-result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          cursor: pointer;
          border-bottom: 1px solid #e2e8f0;
          transition: background 0.2s;
        }

        .search-result-item:hover {
          background: #f8fafc;
        }

        .search-result-item.out-of-stock {
          opacity: 0.6;
          cursor: not-allowed;
          background: #fef2f2;
        }

        .search-result-item.out-of-stock:hover {
          background: #fef2f2;
        }

        .result-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .result-info {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .result-code {
          font-size: 0.875rem;
          color: #64748b;
        }

        .result-stock {
          font-size: 0.875rem;
          color: #10b981;
          font-weight: 600;
        }

        .result-stock.no-stock {
          color: #ef4444;
        }

        .result-price-container {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.25rem;
        }

        .result-price {
          font-weight: 700;
          color: #10b981;
        }

        .no-stock-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 4px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .client-section {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .client-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .toggle-client-btn {
          font-size: 0.875rem;
        }

        .client-input {
          width: 100%;
        }

        .cart-section {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 8px;
        }

        .cart-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .cart-empty {
          text-align: center;
          padding: 2rem;
          color: #94a3b8;
        }

        .cart-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .cart-item-wrapper {
          background: white;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .cart-item {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 1rem;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .cart-item-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .cart-item-name {
          font-weight: 600;
          color: #1e293b;
        }

        .cart-item-price {
          font-size: 0.875rem;
          color: #64748b;
        }

        .cart-item-controls {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .quantity-input {
          width: 80px;
          text-align: center;
        }

        .cart-item-total {
          font-weight: 700;
          color: #10b981;
          font-size: 1.125rem;
        }

        .discount-controls {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
        }

        .discount-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
          margin: 0;
        }

        .discount-type-select {
          width: 160px;
        }

        .discount-value-input {
          width: 120px;
        }

        .discount-badge {
          padding: 0.25rem 0.75rem;
          background: #dcfce7;
          color: #15803d;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .payment-section {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .total-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          background: #f1f5f9;
          border-radius: 8px;
        }

        .total-label {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
        }

        .total-amount {
          font-size: 2rem;
          font-weight: 800;
          color: #10b981;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        /* Annul Modal Styles */
        .annul-modal {
          max-width: 500px;
          z-index: 9999;
        }

        .annul-modal-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .annul-warning-text {
          font-size: 1rem;
          color: #475569;
          text-align: center;
          margin: 0;
        }

        .sale-info-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .sale-info-box p {
          margin: 0;
          font-size: 0.9375rem;
          color: #475569;
        }

        .sale-info-box strong {
          color: #1e293b;
        }

        .annul-modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .confirm-annul-button {
          background: #f59e0b;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }

        .confirm-annul-button:hover {
          background: #d97706;
        }

        .confirm-annul-button:disabled {
          background: #fbbf24;
          opacity: 0.5;
          cursor: not-allowed;
        }


        /* Delete Modal Styles */
        .delete-modal {
          max-width: 500px;
          z-index: 9999;
        }

        .delete-modal-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .delete-warning-text {
          font-size: 1rem;
          color: #dc2626;
          text-align: center;
          margin: 0;
          font-weight: 600;
          padding: 1rem;
          background: #fef2f2;
          border: 2px solid #fecaca;
          border-radius: 8px;
        }

        .delete-modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .confirm-delete-button {
          background: #dc2626;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }

        .confirm-delete-button:hover {
          background: #991b1b;
        }

        .confirm-delete-button:disabled {
          background: #ef4444;
          opacity: 0.5;
          cursor: not-allowed;
        }


        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .cart-item {
            grid-template-columns: 1fr;
          }

          .cart-item-total {
            text-align: right;
          }

          .discount-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .discount-type-select,
          .discount-value-input {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Sales;
